import {
  DeleteFilled,
  InfoCircleFilled,
  UserOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  DELETE_SUGGESTED_REVIEWER,
  ErrorResponse,
  GET_IDEA_MEMBERS,
  GET_IDEA_REVIEW_REQUESTS,
  GET_IDEA_REVIEWER_LIST,
  LIST_SUGGESTED_REVIEWERS,
  Permission,
  SEND_IDEA_REVIEWER_REQUEST,
  SETTLE_IDEA_REVIEWER_REQUEST,
  SuggestedReviewer,
  UNASSIGN_CHANNEL_IDEA_MEMBER,
  User,
} from '@frontend/data-access';
import { UserAvatar } from '@frontend/shared/ui';
import { getLinkLabel } from '@frontend/shared/util';
import { App, Avatar, Button, List, Segmented } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import IdeaInviteReviewer from './Invite';

/* eslint-disable-next-line */
export interface IdeaChannelReviewersProps {
  ideaId: string;
}

type TabType = 'ASSIGNED' | 'INVITED';

// Define response types for the queries
interface IdeaMember {
  user: User;
  permission: Permission;
}

interface ReviewRequest {
  user: User;
  requestedBy: User;
}

export const IdeaChannelReviewers = ({ ideaId }: IdeaChannelReviewersProps) => {
  const { notification } = App.useApp();
  const router = useRouter();
  const channelId = router.query['id'] as string | undefined;
  const [tab, setTab] = useState<TabType>('ASSIGNED');
  const { t } = useTranslation('common');

  const tabItems = [
    { label: t('assigned_reviewers'), value: 'ASSIGNED' },
    { label: t('invited_reviewers'), value: 'INVITED' },
  ];

  const {
    data: suggestedReviewersData,
    loading: reviewersLoading,
    refetch,
  } = useQuery<{ listSuggestedReviewers: SuggestedReviewer[] }>(
    LIST_SUGGESTED_REVIEWERS,
    {
      variables: { ideaId },
      fetchPolicy: 'no-cache',
    }
  );

  const [getMembers, { data: membersData, loading: membersLoading }] =
    useLazyQuery<{
      getIdeaMembers: IdeaMember[];
    }>(GET_IDEA_MEMBERS, {
      fetchPolicy: 'no-cache',
      variables: { ideaId },
    });

  const [getRequests, { data: requestsData, loading: requestsLoading }] =
    useLazyQuery<{
      ideaReviewRequests: ReviewRequest[];
    }>(GET_IDEA_REVIEW_REQUESTS, {
      fetchPolicy: 'no-cache',
      variables: { ideaId },
    });

  const fetchData = useCallback(() => {
    if (tab === 'ASSIGNED') getMembers();
    else if (tab === 'INVITED') getRequests();
  }, [tab, getMembers, getRequests]);

  const handleAddCompleted = () => {
    setTab('INVITED');
    fetchData();
    refetch();
  };

  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: error.message,
    });
  };
  const [unAssignMember] = useMutation(UNASSIGN_CHANNEL_IDEA_MEMBER, {
    onCompleted: fetchData,
    onError: handleError,
  });
  const [removeRequest] = useMutation(SETTLE_IDEA_REVIEWER_REQUEST, {
    onCompleted: fetchData,
    onError: handleError,
  });

  const [sendInvite] = useMutation(SEND_IDEA_REVIEWER_REQUEST, {
    onCompleted: () => {
      setTab('INVITED');
      fetchData();
      refetch();
    },
    onError: handleError,
    refetchQueries: [
      {
        query: GET_IDEA_REVIEWER_LIST,
        variables: {
          input: {
            channelId,
            ideaId,
          },
        },
      },
    ],
  });

  const [removeItem] = useMutation(DELETE_SUGGESTED_REVIEWER, {
    onCompleted() {
      refetch();
    },
    onError: handleError,
  });

  const handleClickAdd = (id: string, userId: number) => {
    removeItem({ variables: { id } });
    sendInvite({
      variables: {
        input: {
          userId: userId,
          channelId,
          ideaId,
        },
      },
    });
  };

  const handleRemoveClick = (userId: number) => {
    const mutation = tab === 'ASSIGNED' ? unAssignMember : removeRequest;
    const variables =
      tab === 'ASSIGNED'
        ? {
            input: {
              channelId,
              ideaId,
              userId,
              memberType: 'CHANNEL_REVIEWER',
            },
          }
        : { input: { ideaId, userId, response: false } };

    mutation({ variables });
  };

  const handleRemoveSuggested = (id: string) => {
    removeItem({ variables: { id } });
  };

  const renderListData = (): {
    user: User;
    permission?: Permission;
  }[] => {
    return tab === 'ASSIGNED'
      ? membersData?.getIdeaMembers || []
      : requestsData?.ideaReviewRequests.map((req) => ({ user: req.user })) ||
          [];
  };

  const handleCopy = async (text: string) => {
    if ('clipboard' in navigator) {
      await navigator.clipboard.writeText(text);
      notification.success({
        message: t('copied_clipboard'),
      });
    }
  };

  const renderReviewerItem = (item: SuggestedReviewer) => (
    <List.Item className="flex-col px-2 py-3 rounded-md mb-2 bg-gray-300 gap-2">
      <div className="w-full flex justify-between gap-2">
        <div className="flex items-center gap-2">
          <Avatar size={32} icon={<UserOutlined />} />
          <div className="text-sm">
            <div className="text-gray-900">
              {item.firstName} {item.lastName}
            </div>
            <div className="text-gray-700">{item.email}</div>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {item.userId ? (
            <Button
              shape="round"
              size="small"
              onClick={() => handleClickAdd(item.id, item.userId)}
            >
              {t('assign')}
            </Button>
          ) : (
            <Button
              shape="round"
              size="small"
              onClick={() => handleCopy(item.email)}
            >
              {t('copy_email')}
            </Button>
          )}
          <Button
            size="small"
            shape="circle"
            onClick={() => handleRemoveSuggested(item.id)}
          >
            <DeleteFilled />
          </Button>
        </div>
      </div>
      {item.description && (
        <div className="w-full">
          {t('note')}: {item.description}
        </div>
      )}
    </List.Item>
  );

  useEffect(() => {
    fetchData();
  }, [tab, fetchData]);

  return (
    <div className={!channelId ? 'cursor-not-allowed' : ''}>
      <div className={!channelId ? 'opacity-50 pointer-events-none' : ''}>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>{t('reviewers')}</div>
          </div>
          <div className="p-4 rounded-md border border-gray-400 bg-gray-200 flex space-x-2">
            <InfoCircleFilled className="text-gray-700" />
            <span className="text-gray-1000">{t('assign_reviewers')}</span>
          </div>
          <IdeaInviteReviewer
            channelId={channelId as string}
            ideaId={ideaId}
            onFinish={handleAddCompleted}
          />
          <Segmented
            rootClassName="overflow-x-auto"
            options={tabItems}
            value={tab}
            onChange={(value) => setTab(value as TabType)}
          />

          <List
            size="small"
            loading={membersLoading || requestsLoading}
            dataSource={renderListData()}
            split={false}
            renderItem={(item) => (
              <List.Item className="!p-0 mb-2">
                <div className="flex items-center gap-2">
                  <UserAvatar user={item.user} size={32} />
                  <div className="text-sm">
                    <div className="text-gray-900">{item.user.fullName}</div>
                    <div className="text-gray-700">{item.user.email}</div>
                  </div>
                </div>
                <div className="flex gap-3 items-center">
                  {(item.permission?.resource.name === 'CHANNEL_EXPERT' ||
                    item.permission?.resource.name === 'CHANNEL_REVIEWER') && (
                    <div className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-sm text-nowrap">
                      {getLinkLabel(
                        item.permission?.resource.name.toLowerCase()
                      )}
                    </div>
                  )}
                  {tab === 'INVITED' && (
                    <div className="px-2 py-1 bg-orange-100 text-orange-600 text-xs rounded-sm">
                      {t('pending')}
                    </div>
                  )}
                  {item.permission?.resource.name !== 'CHANNEL_EXPERT' &&
                    item.permission?.resource.name !== 'CHANNEL_REVIEWER' && (
                      <Button
                        shape="circle"
                        size="small"
                        disabled={membersLoading || requestsLoading}
                        onClick={() => handleRemoveClick(item.user.id)}
                      >
                        <DeleteFilled />
                      </Button>
                    )}
                </div>
              </List.Item>
            )}
          />

          <div className="space-y-2">
            <div className="text-gray-700">{t('suggested_reviewers')}</div>
            <List
              size="small"
              loading={reviewersLoading}
              dataSource={suggestedReviewersData?.listSuggestedReviewers}
              split={false}
              renderItem={renderReviewerItem}
            />
          </div>
        </div>
      </div>
      {!channelId && (
        <div className="text-center text-gray-800 mt-4">
          {t('create_channel_first')}
        </div>
      )}
    </div>
  );
};

export default IdeaChannelReviewers;
